import type { Transaction } from "../../types";

interface Props {
  transaction: any;
  locationName: string;
  onClose: () => void;
}

// Simple Code128 barcode generator (subset B) as SVG
function generateBarcodeSVG(text: string): string {
  // Use a basic visual representation - in production you'd use a library
  const bars = text.split("").map((char, i) => {
    const code = char.charCodeAt(0);
    const width = 2;
    const x = i * 4 + 10;
    return `<rect x="${x}" y="0" width="${width}" height="40" fill="${code % 2 === 0 ? '#000' : '#000'}" opacity="${0.4 + (code % 7) * 0.1}"/>
            <rect x="${x + 2}" y="0" width="1" height="40" fill="#fff"/>`;
  }).join("");
  const totalWidth = text.length * 4 + 20;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${totalWidth} 50" width="${totalWidth}" height="50">
    <rect width="${totalWidth}" height="50" fill="white"/>
    ${bars}
    <text x="${totalWidth / 2}" y="48" text-anchor="middle" font-size="8" font-family="monospace">${text}</text>
  </svg>`;
}

export default function ReceiptModal({ transaction, locationName, onClose }: Props) {
  const txn = transaction;
  const receiptNum = txn.receiptNumber || txn.transactionId?.slice(0, 8) || "N/A";

  const handlePrint = () => {
    const printWindow = window.open("", "_blank", "width=400,height=700");
    if (!printWindow) return;
    const barcodeSvg = generateBarcodeSVG(receiptNum);
    printWindow.document.write(`
      <html><head><title>Receipt ${receiptNum}</title>
      <style>
        body { font-family: monospace; padding: 20px; max-width: 300px; margin: 0 auto; font-size: 12px; }
        .center { text-align: center; }
        .line { border-top: 1px dashed #000; margin: 8px 0; }
        .row { display: flex; justify-content: space-between; }
        .bold { font-weight: bold; }
        .barcode { text-align: center; margin: 10px 0; }
      </style></head><body>
      <div class="center"><strong>RECEIPT</strong></div>
      <div class="center">${locationName}</div>
      <div class="center">${new Date(txn.createdAt).toLocaleString()}</div>
      <div class="center" style="margin-top:4px;font-size:14px;font-weight:bold">#${receiptNum}</div>
      <div class="line"></div>
      ${(txn.items || []).map((i: any) => `<div class="row"><span>${i.quantity}x ${i.name}</span><span>$${(i.price * i.quantity).toFixed(2)}</span></div>`).join("")}
      <div class="line"></div>
      <div class="row"><span>Subtotal</span><span>$${(txn.subtotal || 0).toFixed(2)}</span></div>
      <div class="row"><span>Tax</span><span>$${(txn.tax || 0).toFixed(2)}</span></div>
      <div class="row bold"><span>TOTAL</span><span>$${(txn.total || 0).toFixed(2)}</span></div>
      <div class="line"></div>
      <div class="center">Payment: ${(txn.paymentMethod || "").toUpperCase()}</div>
      <div class="barcode">${barcodeSvg}</div>
      <div class="center" style="font-size:10px;">Thank you!</div>
      </body></html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "400px" }}>
        <div style={{ textAlign: "center", marginBottom: "1rem" }}>
          <h3 style={{ marginBottom: "0.25rem" }}>Receipt</h3>
          <p style={{ color: "#6b7280", fontSize: "0.85rem" }}>{locationName}</p>
          <p style={{ color: "#6b7280", fontSize: "0.8rem" }}>{new Date(txn.createdAt).toLocaleString()}</p>
          <p style={{ fontWeight: 700, fontSize: "1.1rem", marginTop: "0.5rem" }}>#{receiptNum}</p>
        </div>

        <div style={{ borderTop: "1px dashed #d1d5db", borderBottom: "1px dashed #d1d5db", padding: "0.75rem 0", margin: "0.75rem 0" }}>
          {(txn.items || []).map((item: any, i: number) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "0.25rem 0", fontSize: "0.9rem" }}>
              <span>{item.quantity}× {item.name}</span>
              <span>${(item.price * item.quantity).toFixed(2)}</span>
            </div>
          ))}
        </div>

        <div style={{ padding: "0.5rem 0" }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.85rem", color: "#6b7280" }}>
            <span>Subtotal</span><span>${(txn.subtotal || 0).toFixed(2)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.85rem", color: "#6b7280" }}>
            <span>Tax</span><span>${(txn.tax || 0).toFixed(2)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "1.1rem", fontWeight: 700, marginTop: "0.5rem", paddingTop: "0.5rem", borderTop: "1px solid #e5e7eb" }}>
            <span>Total</span><span>${(txn.total || 0).toFixed(2)}</span>
          </div>
        </div>

        <div style={{ textAlign: "center", margin: "0.75rem 0", color: "#6b7280", fontSize: "0.85rem" }}>
          Payment: {txn.paymentMethod === "cash" ? "💵 Cash" : txn.paymentMethod === "card" ? "💳 Card" : txn.paymentMethod === "gift-card" ? "🎁 Gift Card" : "📝 " + txn.paymentMethod}
        </div>


        {/* Returns history */}
        {txn.returns?.length > 0 && (
          <div style={{ borderTop: "1px dashed #fecaca", marginTop: "0.75rem", paddingTop: "0.75rem" }}>
            <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "#dc2626", marginBottom: "0.5rem" }}>Returns</div>
            {txn.returns.map((ret: any, i: number) => (
              <div key={i} style={{ marginBottom: "0.5rem", fontSize: "0.8rem", color: "#6b7280" }}>
                <div>{new Date(ret.returnedAt).toLocaleString()} — Refund: ${ret.refundAmount?.toFixed(2)}</div>
                {ret.items?.map((item: any, j: number) => (
                  <div key={j} style={{ paddingLeft: "0.75rem" }}>↩ {item.quantity}× {item.name}</div>
                ))}
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 600, fontSize: "0.9rem", color: "#dc2626", marginTop: "0.5rem", paddingTop: "0.5rem", borderTop: "1px solid #fecaca" }}>
              <span>Total Refunded</span><span>-${(txn.refundedTotal || 0).toFixed(2)}</span>
            </div>
          </div>
        )}
        {/* Barcode */}
        <div style={{ textAlign: "center", margin: "1rem 0" }} dangerouslySetInnerHTML={{ __html: generateBarcodeSVG(receiptNum) }} />

        <div className="modal-actions">
          <button type="button" className="btn-cancel" onClick={onClose}>Close</button>
          <button type="button" className="btn btn-primary" style={{ width: "auto" }} onClick={handlePrint}>🖨️ Print</button>
        </div>
      </div>
    </div>
  );
}
